/**
 * Report Studio — the saved shape of a report.
 *
 * A report is what was on the screen after an analysis, kept as it was: its tables (the analysis' results,
 * copied in, so the report opens without running the analysis again), the visuals made from them, where each
 * visual sits on the canvas, and a snapshot of the map at that moment. It is stored in a workspace as a
 * gx.report document (definition = this JSON).
 */
import type { CellValue, ExportChartType, ValueFormat } from './export/bundle';

export const REPORT_FORMAT = 1;

export interface Field {
  key: string;
  label: string;
  format: ValueFormat;
}

/** Where a table came from in the analysis. */
export interface DatasetOrigin {
  point: string;
  travel: string;
  time: string;
  layer: string;
}

/** One table of the analysis, as it came. */
export interface SourceDataset {
  id: string;
  kind: 'source';
  title: string;
  origin?: DatasetOrigin;
  fields: Field[];
  rows: Record<string, CellValue>[];
}

/** Several tables side by side, their rows matched on one field (CatchmentTime, say). */
export interface JoinDataset {
  id: string;
  kind: 'join';
  title: string;
  sources: string[];
  key: string;
}

/** Several tables one under another, each row marked with the table, point, travel type and time it came from. */
export interface AppendDataset {
  id: string;
  kind: 'append';
  title: string;
  sources: string[];
}

export type Dataset = SourceDataset | JoinDataset | AppendDataset;

export type Aggregation = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'distinct' | 'first';

export interface Measure {
  field: string;
  agg: Aggregation;
}

/** Keep only the rows whose value of `field` is one of `values` (as text). */
export interface Filter {
  field: string;
  values: string[];
}

interface VisualBase {
  id: string;
  title: string;
}

interface DataVisual extends VisualBase {
  datasetId: string;
  filters: Filter[];
  /** Leave out the analysis' "Total" rows. */
  excludeTotals: boolean;
}

export interface TableVisual extends DataVisual {
  kind: 'table';
  /** Which fields, in which order; empty = all. */
  columns: string[];
}

export interface PivotVisual extends DataVisual {
  kind: 'pivot';
  rows: string[];
  columns: string[];
  measure: Measure;
  totals: boolean;
}

export type ChartType = ExportChartType;

export interface ChartVisual extends DataVisual {
  kind: 'chart';
  chartType: ChartType;
  /** The axis: one point per value of this field. */
  category: string;
  /** The legend: one series per value of this field (a cross table). */
  series: string;
  measures: Measure[];
  /**
   * rows: a point per row (per category value), a series per measure.
   * columns: a point per measure (the table's columns become the axis), a series per row.
   */
  orientation: 'rows' | 'columns';
  sort: 'none' | 'asc' | 'desc';
  topN: number;
  showLabels: boolean;
  palette: string[];
}

export interface MapVisual extends VisualBase {
  kind: 'map';
}

export interface TextVisual extends VisualBase {
  kind: 'text';
  text: string;
}

export type Visual = TableVisual | PivotVisual | ChartVisual | MapVisual | TextVisual;
export type DataVisualT = TableVisual | PivotVisual | ChartVisual;

export interface Placement {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface MapSnapshot {
  /** JPEG data URL. */
  image: string;
  width: number;
  height: number;
  /** lon/lat */
  center?: [number, number];
  zoom?: number;
  /** The layers that were on the map. */
  layers: string[];
  takenAt: string;
}

export interface ReportDoc {
  format: number;
  kind: 'catchment';
  name: string;
  orientation: 'portrait' | 'landscape';
  datasets: Dataset[];
  visuals: Visual[];
  layout: Placement[];
  map?: MapSnapshot;
  createdAt: string;
}

export function isDataVisual(v: Visual): v is DataVisualT {
  return v.kind === 'table' || v.kind === 'pivot' || v.kind === 'chart';
}

export const PALETTE = [
  '#2563eb', '#e11d48', '#16a34a', '#d97706', '#7c3aed',
  '#0891b2', '#db2777', '#4d7c0f', '#ea580c', '#4f46e5',
  '#0d9488', '#9333ea',
];

/** Canvas width per orientation: an A4 page's printable width at 96 dpi, give or take. */
export const PAGE = {
  landscape: { width: 1060, height: 640 },
  portrait: { width: 740, height: 980 },
} as const;

export function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
